// Closing tab Step 3 (Escrow Release), for either escrow model.
//
// Owner ruling 5 (2026-10-08): an Escrow.com deal releases here too, once its
// transaction reached "buyer funded" (re-checked by the server), and the server
// records the release on the transaction. Whether the step is funded and
// released comes from the server's shared predicate (useDealEscrowStatus); the
// legacy row and the Escrow.com transaction only supply the facts shown.

import { useState } from "react";
import { ExternalLink } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  useDealEscrowStatus,
  useDealEscrowTransaction,
  useEscrowSetup,
  useReleaseEscrow,
} from "@/hooks/dealFlow/useDealFlow";
import { formatCurrency } from "@/design-system/lib/format";
import { BypassBanner } from "../../gateBypass/BypassBanner";
import { isoDateInTz, useDateFormatter } from "@/utils/datetime";

interface EscrowReleaseStepProps {
  dealId: string;
  readOnly?: boolean;
}

export function EscrowReleaseStep({ dealId, readOnly = false }: EscrowReleaseStepProps) {
  const status = useDealEscrowStatus(dealId);
  const isEscrowCom = status.data?.model === "EscrowCom";
  const escrow = useEscrowSetup(dealId);
  const escrowCom = useDealEscrowTransaction(isEscrowCom ? dealId : undefined);
  const release = useReleaseEscrow(dealId);
  const toast = useToast();
  const { date, tz } = useDateFormatter();

  const [modalOpen, setModalOpen] = useState(false);
  const [releasedAt, setReleasedAt] = useState(() =>
    isoDateInTz(new Date(), tz),
  );
  const [releasedAmount, setReleasedAmount] = useState("");
  const [txnRef, setTxnRef] = useState("");

  const data = escrow.data;
  const transaction = escrowCom.transaction;
  const fundingVerified = !!status.data?.fundingVerified;
  const released = !!status.data?.released;

  const onOpenModal = () => {
    setReleasedAt(isoDateInTz(new Date(), tz));
    setReleasedAmount(String((isEscrowCom ? transaction?.amount : data?.amountUsd) ?? ""));
    setTxnRef("");
    setModalOpen(true);
  };

  const onConfirm = async () => {
    if (!txnRef.trim()) {
      toast.error("Transaction reference is required.");
      return;
    }
    const amount = Number(releasedAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Released amount must be positive.");
      return;
    }
    try {
      await release.mutateAsync({
        releasedAt: new Date(releasedAt + "T00:00:00.000Z").toISOString(),
        releasedAmount: amount,
        transactionReference: txnRef.trim(),
      });
      toast.success("Escrow released.");
      setModalOpen(false);
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not release escrow.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-space-3" data-testid="closing-step-3">
      {data?.bypassedAt && (
        <BypassBanner bypassedAt={data.bypassedAt} reason={data.bypassReason} />
      )}
      <Card>
        <header className="flex items-start justify-between gap-space-3 mb-space-3">
          <div>
            <h3 className="font-display text-h4 text-onyx m-0">
              Step 3 · Release Escrow into Phoenix Account
            </h3>
            <p className="text-caption text-gray-mid m-0">
              {isEscrowCom
                ? "Escrow.com transaction — record the release here once the funds reach Phoenix."
                : "Manual escrow.com action — certify here after releasing."}
            </p>
          </div>
          {released ? (
            <Badge status="gold">Released</Badge>
          ) : fundingVerified ? (
            <Badge status="review">Ready to release</Badge>
          ) : (
            <Badge status="archived">Awaiting funding verification</Badge>
          )}
        </header>

        {isEscrowCom && (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-space-3 mb-space-3" data-testid="closing-step-3-escrowcom">
            <Field label="Escrow.com transaction">
              {transaction?.externalUrl ? (
                <a
                  href={transaction.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold-shadow underline inline-flex items-center gap-space-1"
                >
                  {transaction.escrowComTransactionId ?? "Open escrow.com"}
                  <ExternalLink size={12} aria-hidden />
                </a>
              ) : (
                <span>{transaction?.escrowComTransactionId ?? "—"}</span>
              )}
            </Field>
            <Field label="Escrow amount">
              {transaction
                ? formatCurrency(transaction.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : "—"}
            </Field>
            <Field label="Funded by buyer">
              {fundingVerified ? "Buyer funded on Escrow.com" : "Not yet funded"}
            </Field>
            <Field label="Released to Phoenix">
              {status.data?.releasedAt
                ? `Released ${formatCurrency(status.data.releasedAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} on ${date(status.data.releasedAt)}`
                : "Not yet released"}
            </Field>
          </dl>
        )}

        {!isEscrowCom && data && (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-space-3 mb-space-3">
            <Field label="Escrow.com link">
              {data.escrowComLink ? (
                <a
                  href={data.escrowComLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold-shadow underline inline-flex items-center gap-space-1"
                >
                  Open escrow.com
                  <ExternalLink size={12} aria-hidden />
                </a>
              ) : (
                <span className="text-gray-mid">No link captured</span>
              )}
            </Field>
            <Field label="Escrow amount">
              ${data.amountUsd.toLocaleString()}
            </Field>
            <Field label="Funded by buyer">
              {data.fundingVerifiedAt
                ? `Verified by A1 on ${date(data.fundingVerifiedAt)}`
                : "Not yet verified"}
            </Field>
            <Field label="Released to Phoenix">
              {data.releasedAt
                ? `Released $${data.releasedAmount?.toLocaleString() ?? "?"} on ${date(data.releasedAt)}`
                : "Not yet released"}
            </Field>
          </dl>
        )}

        {!released && !readOnly && (
          <div className="flex items-center gap-space-2">
            <Button
              variant="primary"
              onClick={onOpenModal}
              disabled={!fundingVerified || release.isPending}
              title={
                fundingVerified
                  ? undefined
                  : isEscrowCom
                    ? "The buyer has not funded the Escrow.com transaction — escrow cannot be released."
                    : "Funding has not been verified — escrow cannot be released."
              }
            >
              Release escrow…
            </Button>
            {!fundingVerified && (
              <p className="text-caption text-gray-mid m-0">
                {isEscrowCom
                  ? "The buyer must fund the Escrow.com transaction before release."
                  : "A1 must verify funding in Underwriting Section 2 before release."}
              </p>
            )}
          </div>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        dismissOnBackdropClick={false}
        title="Release escrow"
        description={
          isEscrowCom
            ? "Record the release of the Escrow.com transaction into Phoenix's account. This cannot be changed once recorded."
            : "After releasing escrow at escrow.com, certify the release here. This row is immutable once signed."
        }
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={release.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void onConfirm()}
              disabled={release.isPending}
            >
              {release.isPending ? "Releasing…" : "Confirm release"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-space-3">
          <FormField label="Released on (date)">
            <Input
              type="date"
              value={releasedAt}
              onChange={(e) => setReleasedAt(e.target.value)}
              // The upper bound is "not in the future", and future is the
              // VIEWER's question. Read as the UTC day it offered TOMORROW from
              // 17:00 Phoenix, on a field that becomes the loan's close date.
              // Unlike the three bounds BOUNDS_AWAITING_SERVER_SIDE held until
              // their server rules moved, this one waits on nobody:
              // EscrowSetupService.ReleaseLegacyAsync constrains only the
              // lower end, so no server rule mirrors it.
              max={isoDateInTz(new Date(), tz)}
              // NOT converted: hydrating a stored value, which is the correct
              // direction for a `.slice(0, 10)`.
              min={data?.fundingVerifiedAt?.slice(0, 10)}
            />
          </FormField>
          <FormField label="Released amount (USD)">
            <Input
              inputMode="decimal"
              value={releasedAmount}
              onChange={(e) => setReleasedAmount(e.target.value)}
            />
          </FormField>
          <FormField label="Escrow.com transaction reference">
            <Input
              value={txnRef}
              maxLength={100}
              onChange={(e) => setTxnRef(e.target.value)}
              placeholder="e.g. ESC-2026-00123"
            />
          </FormField>
        </div>
      </Modal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-display text-label uppercase tracking-label text-gold-shadow m-0">
        {label}
      </dt>
      <dd className="text-small text-onyx m-0 mt-space-1">{children}</dd>
    </div>
  );
}
